import React from 'react';
import { Link } from 'react-router-dom';
import {
  Truck, Clock, Smartphone, MapPin, Gauge, AlertTriangle, FileText, BellRing, ArrowRight, CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DemoVideoButton } from '@/components/DemoVideoButton';

const FEATURES = [
  { icon: Clock, title: 'Schedule every pick-up & drop-off', text: 'Assign children to each van, set AM pick-up and PM drop-off addresses and times, then sort by time or optimize by distance in one click.' },
  { icon: Smartphone, title: 'Driver app on any phone', text: 'Drivers sign in with a quick 4-digit PIN. No app store, no install, works on the phone they already carry.' },
  { icon: CheckCircle, title: 'Tap kids on & off the van', text: "Today's manifest in route order. Each tap updates attendance automatically, so ratios and DCF records stay accurate." },
  { icon: MapPin, title: 'Live route map', text: 'Every stop on a map for the driver, plus a live view of the van for the center.' },
  { icon: BellRing, title: 'Parent ETA texts', text: 'Parents get a text when their child is on the van, when the van is next, and when they arrive.' },
  { icon: Gauge, title: 'Speed alerts', text: 'The app tracks speed and alerts the center the moment a van goes over your posted limit.' },
  { icon: AlertTriangle, title: 'Hard-brake & harsh-turn detection', text: "The phone's motion sensors flag hard braking, harsh turns and fast acceleration as they happen." },
  { icon: FileText, title: 'DCF trip logs', text: 'Every run is logged with times, children and stops, ready for your licensing visit.' },
];

const STEPS = [
  ['Add your drivers & vans', 'Create a driver login, add the vehicle and plate, and set a PIN.'],
  ['Build the routes', "Assign children and set each child's pick-up and drop-off time and address."],
  ['Drive & stay informed', 'Drivers run the manifest from their phone; you get attendance, ETAs and safety alerts.'],
];

const Nav = () => (
  <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
      <Link to="/" className="flex items-center gap-2" data-testid="transport-nav-home">
        <img src="/icons/icon-192x192.png?v=4" alt="CareKids WI" className="w-11 h-11 object-contain" />
        <span className="font-heading font-bold text-xl">CareKids WI</span>
      </Link>
      <div className="flex items-center gap-3">
        <Link to="/login"><Button variant="ghost" className="font-semibold" data-testid="transport-login-btn">Sign In</Button></Link>
        <Link to="/pricing"><Button className="btn-primary" data-testid="transport-get-started-btn">Get Started</Button></Link>
      </div>
    </div>
  </nav>
);

const Hero = () => (
  <section className="py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-center">
      <div className="lg:col-span-2 animate-slide-up">
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-semibold text-sm mb-6">
          <Truck className="w-4 h-4" /> For centers with vans
        </span>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
          Every van run, <span className="text-primary">safe and on time</span>
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          Schedule pick-ups and drop-offs, give drivers a simple phone app, and get instant safety alerts, all inside CareKids WI.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/pricing">
            <Button className="btn-primary text-lg px-8 py-6 w-full" data-testid="transport-hero-trial-btn">
              Start 7-day free trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <DemoVideoButton className="btn-secondary text-lg px-8 py-6" label="Watch Full Demo" testId="transport-full-demo-btn" />
        </div>
      </div>
      <div className="lg:col-span-3 animate-fade-in">
        <div className="rounded-3xl overflow-hidden shadow-float bg-black ring-1 ring-black/5">
          <video controls playsInline preload="metadata" poster="/demo/carekids-driver-app-poster.jpg"
            className="w-full aspect-video" data-testid="transport-driver-video">
            <source src="/demo/carekids-driver-app.mp4" type="video/mp4" />
            <source src="/demo/carekids-driver-app.webm" type="video/webm" />
          </video>
        </div>
        <p className="text-sm text-muted-foreground mt-3">45-second tour: route scheduling, the driver app, and safety alerts.</p>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-12 max-w-2xl">Everything your transportation program needs</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {FEATURES.map(({ icon: Icon, title, text }) => (
          <div key={title} className="card-clay p-6 hover:border-primary/30 transition-colors group" data-testid={`transport-feature-${title.split(' ')[0].toLowerCase()}`}>
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-5 group-hover:bg-primary group-hover:text-white transition-colors">
              <Icon className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="py-20">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-12">Up and running in an afternoon</h2>
      <div className="grid md:grid-cols-3 gap-8">
        {STEPS.map(([title, text], i) => (
          <div key={title} className="relative pl-14" data-testid={`transport-step-${i + 1}`}>
            <span className="absolute left-0 top-0 w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center">{i + 1}</span>
            <h3 className="font-heading font-bold text-lg mb-2">{title}</h3>
            <p className="text-muted-foreground">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section className="py-20 bg-primary/5">
    <div className="max-w-3xl mx-auto px-4 text-center">
      <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4">Put your vans on CareKids WI</h2>
      <p className="text-muted-foreground mb-8">Transportation is included with every plan. Try it free for 7 days, and cancel anytime.</p>
      <Link to="/pricing">
        <Button className="btn-primary text-lg px-10 py-6" data-testid="transport-cta-trial-btn">
          Start 7-day free trial <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </section>
);

export default function TransportationPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="transportation-page">
      <Nav />
      <Hero />
      <Features />
      <Steps />
      <Cta />
    </div>
  );
}
